import { createFileRoute } from '@tanstack/react-router';
import type { ReactNode } from 'react';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { Empty } from '@/components/ui/Empty';
import {
  type CostumeDeadlinesReportRow,
  type LeadConversionReportRow,
  type PrivateLessonUtilizationReportRow,
  type ProgramEnrollmentReportRow,
  type RecitalPackageFulfillmentReportRow,
  useCostumeDeadlinesReport,
  useLeadConversionReport,
  usePrivateLessonUtilizationReport,
  useProgramEnrollmentReport,
  useRecitalPackageFulfillmentReport,
} from '@/data/reports';

export const Route = createFileRoute('/admin/reports/')({
  component: AdminReportsIndex,
});

type ReportValue = string | number | null;
interface Column<T> {
  key: string;
  label: string;
  value: (row: T) => ReportValue;
  render?: (row: T) => ReactNode;
}

function titleize(value: string | null | undefined) {
  if (!value) return 'Unspecified';
  return value
    .split('_')
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');
}

function formatSqlDate(value: string | null) {
  if (!value) return 'No due date';
  const [year, month, day] = value.split('-').map(Number);
  if (!year || !month || !day) return value;
  return new Intl.DateTimeFormat(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  }).format(new Date(year, month - 1, day));
}

function toCsvValue(value: ReportValue) {
  if (value === null) return '';
  const text = String(value);
  if (!/[",\r\n]/.test(text)) return text;
  return `"${text.replaceAll('"', '""')}"`;
}

function exportCsv<T>(filename: string, rows: T[], columns: Column<T>[]) {
  const header = columns.map((column) => toCsvValue(column.label)).join(',');
  const body = rows
    .map((row) => columns.map((column) => toCsvValue(column.value(row))).join(','))
    .join('\n');
  const csv = [header, body].filter(Boolean).join('\n');
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  link.click();
  URL.revokeObjectURL(url);
}

function NumberCell({ value }: { value: number }) {
  return <span className="font-medium tabular-nums">{value}</span>;
}

function ReportTable<T>({
  title,
  description,
  filename,
  rows,
  columns,
  isLoading,
  error,
}: {
  title: string;
  description: string;
  filename: string;
  rows: T[];
  columns: Column<T>[];
  isLoading: boolean;
  error: unknown;
}) {
  return (
    <section aria-labelledby={`${filename}-heading`} className="space-y-3">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 id={`${filename}-heading`} className="text-[var(--text-xl)]">
            {title}
          </h2>
          <p className="mt-1 max-w-3xl text-[var(--color-ink-muted)] text-[var(--text-sm)]">
            {description}
          </p>
        </div>
        <Button
          type="button"
          variant="secondary"
          size="sm"
          disabled={rows.length === 0}
          onClick={() => exportCsv(filename, rows, columns)}
        >
          Export {title} CSV
        </Button>
      </div>

      {isLoading && <p aria-live="polite">Loading...</p>}
      {error ? (
        <Empty
          title={`${title} unavailable`}
          description="The report could not be loaded for this session."
        />
      ) : null}
      {!isLoading && !error && rows.length === 0 ? (
        <Empty
          title={`No ${title.toLowerCase()} data`}
          description="New aggregate rows will appear here once matching records exist."
        />
      ) : null}
      {rows.length > 0 ? (
        <div className="surface overflow-x-auto">
          <table className="min-w-full text-left text-[var(--text-sm)]">
            <thead>
              <tr className="border-b border-[var(--color-border)]">
                {columns.map((column) => (
                  <th key={column.key} className="px-4 py-3 font-medium">
                    {column.label}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((row, index) => (
                <tr key={index} className="border-b border-[var(--color-border)] last:border-0">
                  {columns.map((column) => (
                    <td key={column.key} className="px-4 py-3 align-top">
                      {column.render ? column.render(row) : column.value(row)}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : null}
    </section>
  );
}

const leadColumns: Column<LeadConversionReportRow>[] = [
  {
    key: 'lead_status',
    label: 'Status',
    value: (row) => titleize(row.lead_status),
    render: (row) => (
      <Badge tone={row.lead_status === 'enrolled' ? 'success' : 'neutral'}>
        {titleize(row.lead_status)}
      </Badge>
    ),
  },
  { key: 'source', label: 'Source', value: (row) => row.source },
  {
    key: 'lead_count',
    label: 'Leads',
    value: (row) => row.lead_count,
    render: (row) => <NumberCell value={row.lead_count} />,
  },
  {
    key: 'converted_count',
    label: 'Converted',
    value: (row) => row.converted_count,
    render: (row) => <NumberCell value={row.converted_count} />,
  },
];

const programColumns: Column<ProgramEnrollmentReportRow>[] = [
  { key: 'program_type', label: 'Program type', value: (row) => titleize(row.program_type) },
  {
    key: 'active_enrollment_count',
    label: 'Active enrollments',
    value: (row) => row.active_enrollment_count,
    render: (row) => <NumberCell value={row.active_enrollment_count} />,
  },
  {
    key: 'class_count',
    label: 'Classes',
    value: (row) => row.class_count,
    render: (row) => <NumberCell value={row.class_count} />,
  },
  {
    key: 'total_capacity',
    label: 'Capacity',
    value: (row) => row.total_capacity,
    render: (row) => <NumberCell value={row.total_capacity} />,
  },
];

const privateLessonColumns: Column<PrivateLessonUtilizationReportRow>[] = [
  { key: 'program_type', label: 'Type', value: (row) => titleize(row.program_type) },
  { key: 'program_name', label: 'Program', value: (row) => row.program_name },
  { key: 'location_name', label: 'Location', value: (row) => row.location_name },
  { key: 'instructor_name', label: 'Instructor', value: (row) => row.instructor_name },
  {
    key: 'lesson_count',
    label: 'Lessons',
    value: (row) => row.lesson_count,
    render: (row) => <NumberCell value={row.lesson_count} />,
  },
  {
    key: 'active_enrollment_count',
    label: 'Booked',
    value: (row) => row.active_enrollment_count,
    render: (row) => <NumberCell value={row.active_enrollment_count} />,
  },
  {
    key: 'total_capacity',
    label: 'Capacity',
    value: (row) => row.total_capacity,
    render: (row) => <NumberCell value={row.total_capacity} />,
  },
];

const costumeColumns: Column<CostumeDeadlinesReportRow>[] = [
  { key: 'production_name', label: 'Production', value: (row) => row.production_name },
  { key: 'package_name', label: 'Package', value: (row) => row.package_name },
  {
    key: 'due_date',
    label: 'Due date',
    value: (row) => row.due_date,
    render: (row) => formatSqlDate(row.due_date),
  },
  {
    key: 'assignment_count',
    label: 'Assignments',
    value: (row) => row.assignment_count,
    render: (row) => <NumberCell value={row.assignment_count} />,
  },
  {
    key: 'incomplete_count',
    label: 'Incomplete',
    value: (row) => row.incomplete_count,
    render: (row) => (
      <Badge tone={row.incomplete_count > 0 ? 'warn' : 'success'}>{row.incomplete_count}</Badge>
    ),
  },
];

const fulfillmentColumns: Column<RecitalPackageFulfillmentReportRow>[] = [
  { key: 'item_name', label: 'Item', value: (row) => titleize(row.item_name) },
  {
    key: 'item_status',
    label: 'Status',
    value: (row) => titleize(row.item_status),
    render: (row) => (
      <Badge tone={row.item_status === 'delivered' ? 'success' : 'neutral'}>
        {titleize(row.item_status)}
      </Badge>
    ),
  },
  {
    key: 'assignment_count',
    label: 'Assignments',
    value: (row) => row.assignment_count,
    render: (row) => <NumberCell value={row.assignment_count} />,
  },
];

function AdminReportsIndex() {
  const leadConversion = useLeadConversionReport();
  const programEnrollment = useProgramEnrollmentReport();
  const privateLessonUtilization = usePrivateLessonUtilizationReport();
  const costumeDeadlines = useCostumeDeadlinesReport();
  const recitalPackageFulfillment = useRecitalPackageFulfillmentReport();

  return (
    <div className="space-y-10">
      <header>
        <p className="tracking-[var(--tracking-wide)] text-[var(--color-ink-muted)] text-[var(--text-sm)] uppercase">
          Studio intelligence
        </p>
        <h1 className="mt-2">Owner Reports</h1>
      </header>

      <ReportTable
        title="Lead conversion"
        description="Lead volume and converted families by pipeline status and acquisition source."
        filename="lead-conversion.csv"
        rows={leadConversion.data ?? []}
        columns={leadColumns}
        isLoading={leadConversion.isLoading}
        error={leadConversion.error}
      />
      <ReportTable
        title="Program enrollment"
        description="Active enrollment load, class count, and capacity by program type."
        filename="program-enrollment.csv"
        rows={programEnrollment.data ?? []}
        columns={programColumns}
        isLoading={programEnrollment.isLoading}
        error={programEnrollment.error}
      />
      <ReportTable
        title="Private lesson utilization"
        description="Private, semi-private, and trio lesson booking pressure by program, location, and instructor."
        filename="private-lesson-utilization.csv"
        rows={privateLessonUtilization.data ?? []}
        columns={privateLessonColumns}
        isLoading={privateLessonUtilization.isLoading}
        error={privateLessonUtilization.error}
      />
      <ReportTable
        title="Costume deadlines"
        description="Costume package assignment totals and unfinished counts by production, package, and due date."
        filename="costume-deadlines.csv"
        rows={costumeDeadlines.data ?? []}
        columns={costumeColumns}
        isLoading={costumeDeadlines.isLoading}
        error={costumeDeadlines.error}
      />
      <ReportTable
        title="Recital package fulfillment"
        description="Fulfillment counts for included package items across costume, shirt, tickets, video, and program."
        filename="recital-package-fulfillment.csv"
        rows={recitalPackageFulfillment.data ?? []}
        columns={fulfillmentColumns}
        isLoading={recitalPackageFulfillment.isLoading}
        error={recitalPackageFulfillment.error}
      />
    </div>
  );
}
